import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import { OrderingSelectors, OrderingConstants, OrderingOperations } from 'polygon-ordering';
import { setDesiredDeliveryTime } from '../slices/desiredDeliveryTime';
import SubSection from './SubSection';
import TimeSelector from './TimeSelector';
import { logEvent } from '../utils/analytics';
import EVENTS from '../constants/events';
import moment from 'moment';
import { validateFutureMenuItems } from '../thunks/validateFutureMenuItems';
import SectionHeading from './SectionHeading';
import { SALE_TYPE } from '../libs/polygon-ordering/src/constants/saleType';
import SectionContainer from './SectionContainer';
import FutureDateSelector from './FutureDateSelector';
import { RadioToggle } from './RadioToggle';
import { updateForm } from '../slices/deliveryAddressForm';

const {
  getBufferDeliveryTime,
  getAverageAsapDeliveryTime,
  getDeliveryAsapDurationBestCaseScenario,
  getDeliveryAsapDurationWorstCaseScenario,
} = OrderingSelectors;
const { ASAP_TIME } = OrderingConstants;
const { clearBuffer, setDeliveryTimePrevious } = OrderingOperations;

const SelectDeliveryTime = ({ saleType = SALE_TYPE.DELIVERY }: { saleType?: number }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const bufferDeliveryTime = useAppSelector(getBufferDeliveryTime);
  const desiredDeliveryTime = useAppSelector(state => state.desiredDeliveryTime);
  const effectiveDeliveryTime = desiredDeliveryTime || bufferDeliveryTime;
  const deliveryTimeClose = useAppSelector(state => state.config.deliveryTimeClose);

  // Rough estimate delivery for asap
  const averageAsapDeliveryDuration = useAppSelector(getAverageAsapDeliveryTime);
  const roughASAPEstimateDeliveryStringOverride = t('title.deliveryAsapSubTitle');
  const [roughASAPEstimateDelivery, setRoughASAPEstimateDelivery] = useState(
    roughASAPEstimateDeliveryStringOverride,
  );

  const bestCaseScenarioAsapDeliveryDuration = useAppSelector(
    getDeliveryAsapDurationBestCaseScenario,
  );
  const worstCaseScenarioAsapDeliveryDuration = useAppSelector(
    getDeliveryAsapDurationWorstCaseScenario,
  );

  useEffect(() => {
    if (
      worstCaseScenarioAsapDeliveryDuration &&
      bestCaseScenarioAsapDeliveryDuration &&
      worstCaseScenarioAsapDeliveryDuration !== bestCaseScenarioAsapDeliveryDuration
    ) {
      setRoughASAPEstimateDelivery(
        t('title.estimatedBetween', {
          from: bestCaseScenarioAsapDeliveryDuration,
          to: worstCaseScenarioAsapDeliveryDuration,
        }),
      );
    } else if (averageAsapDeliveryDuration > 0) {
      setRoughASAPEstimateDelivery(
        t('title.estimatedUpTo', {
          minutes: bestCaseScenarioAsapDeliveryDuration,
        }),
      );
    }
  }, [
    worstCaseScenarioAsapDeliveryDuration,
    bestCaseScenarioAsapDeliveryDuration,
    averageAsapDeliveryDuration,
    t,
  ]);

  const bestCaseScenarioAsapDeliveryTime = moment().add(
    bestCaseScenarioAsapDeliveryDuration,
    'minutes',
  );

  const earliestTime = bestCaseScenarioAsapDeliveryTime.toISOString();

  const asapAction = () => {
    dispatch(updateForm({ showTimePicker: false }));
    dispatch(clearBuffer({}));
    dispatch(setDeliveryTimePrevious(desiredDeliveryTime));
    dispatch(setDesiredDeliveryTime(ASAP_TIME));
  };

  const options = [
    {
      title: t('timeSummary.asap'),
      subtitle: roughASAPEstimateDelivery,
      action: () => asapAction(),
      Icon: () => <RadioToggle checked={effectiveDeliveryTime === ASAP_TIME} />,
      active: effectiveDeliveryTime === ASAP_TIME,
    },
    {
      title: t('timeSummary.scheduleForLater'),
      subtitle: t('title.chooseTime'),
      action: () => {
        dispatch(updateForm({ showTimePicker: true }));
        dispatch(
          setDesiredDeliveryTime(bestCaseScenarioAsapDeliveryTime.add(1, 'minute').toISOString()),
        );
        dispatch(clearBuffer({}));
      },
      Icon: () => <RadioToggle checked={effectiveDeliveryTime !== ASAP_TIME} />,
      active: effectiveDeliveryTime !== ASAP_TIME,
    },
  ];

  const setDeliveryTime = (value: string | null) => {
    const time = value || ASAP_TIME;
    logEvent(EVENTS.CHANGE_DELIVERY_TIME, { label: time });

    // This is used for the ConfirmChangesModal (only appears when
    // validateFutureMenuItems returns a list of invalid items)
    dispatch(setDeliveryTimePrevious(desiredDeliveryTime));

    dispatch(setDesiredDeliveryTime(time));
    dispatch(validateFutureMenuItems());
    dispatch(clearBuffer({}));
  };

  const formState = useAppSelector(state => state.deliveryAddressForm);

  const isFutureOrdering = saleType === SALE_TYPE.CATERING_DELIVERY;

  useEffect(() => {
    if (!isFutureOrdering && !moment(effectiveDeliveryTime).isSame(moment(), 'day')) {
      asapAction();
    }
  }, []);

  return (
    <>
      <SectionHeading title={t('title.deliveryTimeTitle')} containerStyle={styles.sidePadding} />

      {saleType === SALE_TYPE.DELIVERY && (
        <>
          <SectionContainer style={styles.sidePadding}>
            {options.map((option, index) => (
              <SubSection
                key={index}
                title={option.title}
                subtitle={option.subtitle}
                action={option.action}
                rightSlot={option.Icon}
                active={option.active}
              />
            ))}
            {formState.showTimePicker && (
              <div style={styles.timeSelectorWrapper}>
                <TimeSelector
                  earliestSelectableTime={earliestTime!}
                  latestSelectableDate={deliveryTimeClose!}
                  minuteInterval={5}
                  value={effectiveDeliveryTime}
                  setTime={setDeliveryTime}
                />
              </div>
            )}
          </SectionContainer>
        </>
      )}

      {saleType === SALE_TYPE.CATERING_DELIVERY && (
        <SectionContainer style={styles.sidePadding}>
          <FutureDateSelector
            time={effectiveDeliveryTime}
            setTime={setDeliveryTime}
            // TODO:
            // Ignore all cutoff times for catering delivery only in this scenario
            // because we want the user to still be able to select any time so they can
            // go to any different location. The list of locations (best estimate call)
            // shown is based on the time chosen.
            //
            // This avoids a chicken and egg problem where the user cannot select a
            // time before choosing a desired location because to even get to that, the
            // user needs to select their date and time first to fetch for best
            // estimates.
            //
            // * Note:
            // In phase 2 of the delivery flow there will be this same future date
            // selector on the place order screen under the time summary. For that
            // instance it will not ignore cutoff times because by then the user
            // has already chosen a location.
            //
            /* disableCutoffTimes={true} */
          />
        </SectionContainer>
      )}
    </>
  );
};

const styles: Styles = {
  timeSelectorWrapper: {
    marginTop: 18,
    marginBottom: 18,
    marginLeft: 20,
  },
  sidePadding: {
    paddingLeft: 16,
    paddingRight: 16,
  },
};

export default SelectDeliveryTime;
